import { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Minus, Plus, ShieldCheck, ShoppingCart, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { api, formatApiError } from "../../lib/api";
import { WalletBox, fileToDataUrl } from "./MembershipPanel";
import { Badge, PanelTitle, Table, EmptyRow, VERIFICATION_LABELS, fmtDateTime, money, shortHash } from "./ui";

export const ORDER_STATUS = {
  pendiente: ["pendiente", "Pendiente de pago"],
  en_revision: ["pendiente", "Pago en revisión"],
  pagado: ["activa", "Pago aprobado"],
  entregado: ["activa", "Códigos entregados"],
  rechazado: ["rechazado", "Pago rechazado"],
};

const Cart = ({ price, onCreated }) => {
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);
  const items = price.platforms.map((p) => [p, Number(qty[p] || 0)]).filter(([, n]) => n > 0);
  const totalCodes = items.reduce((a, [, n]) => a + n, 0);
  const set = (p, n) => setQty({ ...qty, [p]: Math.max(0, Math.min(price.max_per_order, n)) });

  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/code-orders", { items: items.map(([platform, count]) => ({ platform, count })) });
      setQty({});
      onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_320px]" data-testid="code-cart">
      <div className="space-y-2">
        {price.platforms.map((p) => (
          <div key={p} className="flex items-center justify-between gap-3 rounded-2xl border border-ink/10 bg-white px-4 py-3" data-testid={`cart-row-${p.replace(" ", "-")}`}>
            <div><p className="font-display text-base font-semibold">{p}</p><p className="text-xs text-ink/60">{money(price.unit_price)} por código · acceso 30 días</p></div>
            <div className="flex items-center gap-2">
              <button onClick={() => set(p, Number(qty[p] || 0) - 1)} className="grid h-9 w-9 place-items-center rounded-full border border-ink/15 hover:bg-paper" aria-label="Menos" data-testid={`cart-minus-${p.replace(" ", "-")}`}><Minus className="h-4 w-4" /></button>
              <Input type="number" min={0} max={price.max_per_order} value={qty[p] ?? ""} placeholder="0" onChange={(e) => set(p, Number(e.target.value))} className="h-9 w-16 bg-white text-center" data-testid={`cart-qty-${p.replace(" ", "-")}`} />
              <button onClick={() => set(p, Number(qty[p] || 0) + 1)} className="grid h-9 w-9 place-items-center rounded-full bg-ink text-white hover:bg-ink-2" aria-label="Más" data-testid={`cart-plus-${p.replace(" ", "-")}`}><Plus className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <aside className="h-fit rounded-[1.5rem] bg-ink p-5 text-white" data-testid="cart-summary">
        <p className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.14em] text-leaf"><ShoppingCart className="h-4 w-4" /> Tu carrito</p>
        <ul className="mt-4 space-y-1.5 text-sm">
          {!items.length && <li className="text-[#AEB7D3]">Agrega códigos de las plataformas que quieras.</li>}
          {items.map(([p, n]) => <li key={p} className="flex justify-between"><span>{p} × {n}</span><span>{money(n * price.unit_price)}</span></li>)}
        </ul>
        <div className="mt-5 border-t border-white/10 pt-4">
          <p className="text-sm text-[#AEB7D3]">Total a pagar</p>
          <p className="font-display text-4xl font-semibold tracking-tight" data-testid="cart-total">{money(totalCodes * price.unit_price)}</p>
          <p className="mt-1 text-xs text-[#AEB7D3]">{totalCodes} código{totalCodes === 1 ? "" : "s"} · USDC o USDT (Polygon)</p>
        </div>
        <button onClick={checkout} disabled={!totalCodes || busy} className="btn-leaf mt-5 w-full disabled:opacity-50" data-testid="cart-checkout">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Ir a pagar"}
        </button>
      </aside>
    </div>
  );
};

const PayOrder = ({ order, onDone, onCancel }) => {
  const [token, setToken] = useState("USDC");
  const [txHash, setTxHash] = useState("");
  const [proof, setProof] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Sube una imagen (captura de pantalla).");
    setProof(await fileToDataUrl(file));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/me/code-orders/${order.id}/payment`, { token, tx_hash: txHash.trim(), proof_image: proof });
      toast[data.status === "pagado" ? "success" : "info"](data.status === "pagado" ? "¡Pago verificado! El administrador te entregará los códigos." : "Comprobante enviado. El administrador lo revisará y te entregará los códigos.");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("¿Cancelar este pedido?")) return;
    await api.post(`/me/code-orders/${order.id}/cancel`);
    onCancel();
  };

  return (
    <div data-testid="order-pay">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-sun/60 bg-sun/15 px-5 py-4">
        <div>
          <p className="font-semibold">Pedido creado: {order.items.map((i) => `${i.platform} × ${i.count}`).join(", ")}</p>
          <p className="text-sm text-ink/75">Envía exactamente <strong>{money(order.total_usd)}</strong> en USDC o USDT por la red Polygon y registra el hash.</p>
        </div>
        <button onClick={cancel} className="inline-flex items-center gap-1.5 text-xs font-semibold text-coral hover:underline" data-testid="order-cancel"><Trash2 className="h-3.5 w-3.5" /> Cancelar pedido</button>
      </div>
      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <WalletBox wallet={order.wallet} token="USDC" />
        <WalletBox wallet={order.wallet} token="USDT" />
      </div>
      <form onSubmit={submit} className="mt-6 rounded-[1.5rem] border border-ink/10 bg-white p-6" data-testid="order-payment-form">
        <p className="font-display text-xl font-semibold">Enviar comprobante al administrador</p>
        <div className="mt-5 grid gap-5 sm:grid-cols-[160px_1fr]">
          <div>
            <label className="mb-2 block text-sm font-semibold">Moneda enviada</label>
            <div className="flex gap-2">{["USDC", "USDT"].map((t) => <button key={t} type="button" onClick={() => setToken(t)} className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-semibold ${token === t ? "border-ink bg-ink text-white" : "border-ink/15 hover:bg-paper"}`} data-testid={`order-token-${t}`}>{t}</button>)}</div>
          </div>
          <div>
            <label htmlFor="otx" className="mb-2 block text-sm font-semibold">Hash de la transacción</label>
            <Input id="otx" required pattern="^0x[0-9a-fA-F]{64}$" placeholder="0x…" value={txHash} onChange={(e) => setTxHash(e.target.value)} className="h-11 bg-white font-mono text-xs" data-testid="order-txhash" />
          </div>
        </div>
        <div className="mt-5">
          <label className="mb-2 block text-sm font-semibold">Captura del pago (opcional)</label>
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-ink/25 px-4 py-3 text-sm text-ink/70 hover:bg-paper">
            <ImagePlus className="h-5 w-5" /> {proof ? "Imagen lista para enviar" : "Seleccionar imagen"}
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="order-proof-input" />
          </label>
        </div>
        <button type="submit" disabled={busy} className="btn-leaf mt-6 disabled:opacity-60" data-testid="order-submit-payment">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />} Enviar comprobante y verificar
        </button>
      </form>
    </div>
  );
};

export const CodeStorePanel = ({ goTo }) => {
  const [price, setPrice] = useState(null);
  const [orders, setOrders] = useState([]);
  const [paying, setPaying] = useState(null);

  const load = useCallback(() => api.get("/me/code-orders").then(({ data }) => setOrders(data)), []);
  useEffect(() => {
    api.get("/public/code-price").then(({ data }) => setPrice(data));
    load();
  }, [load]);

  if (!price) return <Loader2 className="h-6 w-6 animate-spin" />;
  return (
    <div data-testid="code-store-panel">
      <PanelTitle title="Comprar códigos de acceso" text={`Cada código cuesta ${money(price.unit_price)} y da 30 días de acceso a la plataforma. Arma tu carrito, paga en USDC/USDT y envía tu comprobante: el administrador te entregará los códigos en "Mis códigos" y por WhatsApp.`} />
      <div className="mt-6">
        {paying ? <PayOrder order={paying} onDone={() => { setPaying(null); load(); }} onCancel={() => { setPaying(null); load(); }} /> : <Cart price={price} onCreated={(o) => { setPaying(o); load(); }} />}
      </div>

      <h3 className="mt-10 font-display text-xl font-semibold">Mis pedidos</h3>
      <Table headers={["Fecha", "Códigos", "Total", "Estado", "Pago", ""]} testId="orders-table" minWidth="min-w-[820px]">
        {!orders.length && <EmptyRow cols={6} text="Aún no has comprado códigos." testId="orders-empty" />}
        {orders.map((o, i) => (
          <tr key={o.id} className="border-b border-ink/5 align-top last:border-0" data-testid={`order-row-${i}`}>
            <td className="whitespace-nowrap px-4 py-3 text-ink/70">{fmtDateTime(o.created_at)}</td>
            <td className="px-4 py-3 text-sm">{o.items.map((it) => <p key={it.platform}>{it.platform} × {it.count}</p>)}</td>
            <td className="px-4 py-3 font-display text-base font-semibold">{money(o.total_usd)}</td>
            <td className="px-4 py-3"><Badge status={ORDER_STATUS[o.status][0]} label={ORDER_STATUS[o.status][1]} />{o.review_note && <p className="mt-1 text-xs text-ink/60">“{o.review_note}”</p>}</td>
            <td className="px-4 py-3 text-xs text-ink/70">{o.payment ? <>{o.payment.token} · <span className="font-mono" title={o.payment.tx_hash}>{shortHash(o.payment.tx_hash)}</span>{o.payment.verification && <p>{VERIFICATION_LABELS[o.payment.verification.reason] || o.payment.verification.reason}</p>}</> : "—"}</td>
            <td className="px-4 py-3">
              {(o.status === "pendiente" || o.status === "rechazado") && <button onClick={() => setPaying(o)} className="rounded-full bg-ink px-3.5 py-2 text-xs font-semibold text-white" data-testid={`order-pay-${i}`}>Pagar</button>}
              {o.status === "entregado" && <button onClick={() => goTo("codigos")} className="rounded-full border border-ink/15 px-3.5 py-2 text-xs font-semibold hover:bg-paper" data-testid={`order-view-codes-${i}`}>Ver mis códigos</button>}
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
};
